/**
 * The shared vocabulary. Sharp corners, hairline borders, mono for anything that is
 * chrome rather than prose — labels, counts, identifiers, every digit.
 */
import type { ReactNode } from "react";

export type Tone = "ok" | "warn" | "crit" | "info" | "mut" | "gold";

const TONE: Record<Tone, string> = {
  ok: "bg-ok-wash text-ok",
  warn: "bg-warn-wash text-warn",
  crit: "bg-crit-wash text-crit",
  info: "bg-info-wash text-info",
  mut: "bg-hair-soft text-dim",
  gold: "bg-gold-wash text-gold",
};

/** A squared-off status chip. Mono, uppercase, wide-tracked — a readout, not a badge. */
export function Chip({
  tone = "mut",
  children,
  dot = false,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10.5px] font-medium tracking-[0.12em] uppercase ${TONE[tone]}`}
    >
      {dot && <i className="dot" />}
      {children}
    </span>
  );
}

/** `// SECTION` with an optional gold ordinal, the console's section marker. */
export function Eyebrow({ n, children }: { n?: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      {n && <span className="ordinal">{n}</span>}
      <span className="eyebrow">// {children}</span>
    </div>
  );
}

export function Panel({
  title,
  aside,
  actions,
  children,
  bodyClass = "",
  fill = false,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyClass?: string;
  /** Claim the parent's remaining height; the body scrolls, the header stays. */
  fill?: boolean;
}) {
  return (
    <section
      className={`border border-hair bg-surface ${
        fill ? "flex min-h-0 flex-1 flex-col overflow-hidden" : ""
      }`}
    >
      {(title || actions) && (
        <header className="flex min-h-[44px] shrink-0 flex-wrap items-center gap-3 border-b border-hair bg-hair-soft px-4 py-2">
          {title && (
            <h3 className="font-mono text-[11px] font-bold tracking-[0.2em] text-text uppercase">
              {title}
            </h3>
          )}
          {aside}
          {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={`${fill ? "flex min-h-0 flex-1 flex-col overflow-auto" : ""} ${bodyClass}`}>
        {children}
      </div>
    </section>
  );
}

/**
 * A metric. The number is the loudest thing on the tile; the label above it is a mono
 * eyebrow and the note below is the sentence that keeps the number honest.
 */
export function Tile({
  label,
  value,
  note,
  children,
  valueClass = "",
}: {
  label: string;
  value?: ReactNode;
  note?: ReactNode;
  children?: ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex flex-col gap-2.5 bg-surface p-4">
      <span className="eyebrow">{label}</span>
      {value !== undefined && (
        <span
          className={`tnum font-mono text-[30px] leading-none font-bold tracking-tight ${valueClass}`}
        >
          {value}
        </span>
      )}
      {children}
      {note && <span className="text-[11.5px] leading-snug text-dim">{note}</span>}
    </div>
  );
}

/** One number in a horizontal strip. The tile's information without the tile's height. */
export function Metric({
  label,
  value,
  note,
  valueClass = "",
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-2.5 px-3.5 py-2">
      <span className="eyebrow shrink-0">{label}</span>
      <span className={`tnum font-mono text-[17px] leading-none font-bold ${valueClass}`}>
        {value}
      </span>
      {note && <span className="truncate text-[11px] text-faint">{note}</span>}
    </div>
  );
}

export function Button({
  variant = "default",
  children,
  ...rest
}: {
  variant?: "default" | "primary" | "ghost";
  children: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const styles = {
    default: "border border-hair-strong text-text hover:border-gold hover:text-gold",
    primary: "border border-gold-surface bg-gold-surface text-on-gold hover:bg-gold-soft hover:border-gold-soft",
    ghost: "border border-transparent text-dim hover:text-gold",
  }[variant];
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] uppercase transition-colors duration-200 ease-alt disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-hair-strong disabled:hover:text-text ${styles} ${rest.className ?? ""}`}
    >
      {children}
    </button>
  );
}

/** A hairline-ruled toggle group. Squared, mono, one selected. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="flex border border-hair">
      {options.map((option) => (
        <button
          key={option}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`border-s border-hair px-2.5 py-1 font-mono text-[10.5px] tracking-[0.12em] uppercase transition-colors duration-200 ease-alt first:border-s-0 ${
            value === option ? "bg-gold-surface text-on-gold font-bold" : "text-dim hover:text-gold"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export function Empty({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  /** The way out of this empty state — "Upload a document" or "Clear filters". An empty
   *  state with no exit is a dead end with nice typography. */
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <p className="font-mono text-[12px] font-bold tracking-[0.2em] uppercase">{title}</p>
      {hint && <p className="max-w-[52ch] text-[12.5px] leading-relaxed text-dim">{hint}</p>}
      {action && (
        <Button className="mt-2" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function Loading({ what }: { what: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 px-4 py-14 font-mono text-[11px] tracking-[0.2em] text-faint uppercase">
      <i className="dot pulse bg-gold" />
      loading {what}
    </div>
  );
}

/** Errors name what failed and what to do. Never "something went wrong". */
export function Failed({ error, what }: { error: unknown; what: string }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="m-4 border border-crit/40 bg-crit-wash px-4 py-3">
      <p className="font-mono text-[11px] font-bold tracking-[0.16em] text-crit uppercase">
        could not load {what}
      </p>
      <p className="mt-1.5 font-mono text-[12px] break-words text-crit/90">{message}</p>
    </div>
  );
}

export const STATUS_TONE: Record<string, Tone> = {
  succeeded: "ok",
  stale: "warn",
  processing: "gold",
  running: "gold",
  queued: "mut",
  failed: "crit",
  dead: "crit",
  unknown: "mut",
};

export const METHOD_TONE: Record<string, Tone> = {
  text_layer: "gold",
  ocr: "info",
  vlm: "warn",
  profile_derived: "mut",
  human: "ok",
};

export const SEVERITY_TONE: Record<string, Tone> = {
  high: "crit",
  medium: "warn",
  low: "info",
};

/** One colour per read tier, used by every route bar and page map on every screen. */
export const METHOD_COLOR: Record<string, string> = {
  text_layer: "var(--gold)",
  ocr: "var(--info)",
  vlm: "var(--warn)",
  profile_derived: "var(--text-faint)",
  human: "var(--ok)",
};
